import { TWalletData } from '@/features/home/types/WalletData';
import { WalletTypeEnum } from '@/context/types/Wallet';
import { EnumValues } from '@/services/types/EnumValues';
import { ALL_ACCOUNTS } from '@/context/HomeProvider/HomeContextProvider';

export const getWallets = (
  wallets: TWalletData[],
  options: { includeAll: boolean } = { includeAll: false }
): TWalletData[] => (options.includeAll ? wallets : wallets.filter((w) => w.name !== ALL_ACCOUNTS));

export const WalletFilterTypeEnum = {
  TRANSFER: 'TRANSFER',
  PAY: 'PAY',
  XERO_CONNECTION: 'XERO_CONNECTION',
  BATCH: 'BATCH'
} as const;
export type TWalletFilterType = EnumValues<typeof WalletFilterTypeEnum>;

export const WalletFilterDirectionEnum = {
  FROM: 'from',
  TO: 'to'
} as const;
export type TWalletFilterDirection = EnumValues<typeof WalletFilterDirectionEnum>;

const WALLET_FILTER_RULES: Record<
  TWalletFilterType,
  Partial<Record<TWalletFilterDirection, WalletTypeEnum[]>> & { default?: WalletTypeEnum[] }
> = {
  [WalletFilterTypeEnum.TRANSFER]: {
    [WalletFilterDirectionEnum.FROM]: [WalletTypeEnum.EVERYDAY, WalletTypeEnum.GROWTH],
    [WalletFilterDirectionEnum.TO]: [WalletTypeEnum.EVERYDAY, WalletTypeEnum.GROWTH]
  },
  [WalletFilterTypeEnum.PAY]: {
    [WalletFilterDirectionEnum.FROM]: [WalletTypeEnum.EVERYDAY]
  },
  [WalletFilterTypeEnum.XERO_CONNECTION]: {
    default: [WalletTypeEnum.EVERYDAY, WalletTypeEnum.GROWTH]
  },
  [WalletFilterTypeEnum.BATCH]: {
    [WalletFilterDirectionEnum.FROM]: [WalletTypeEnum.EVERYDAY]
  }
};

export const getEligibleWalletTypes = (
  type: TWalletFilterType,
  direction?: TWalletFilterDirection
): WalletTypeEnum[] => {
  const rules = WALLET_FILTER_RULES[type];
  return (direction && rules[direction]) ?? rules.default ?? [];
};

// Pure check against a wallet already in hand - no id lookup, no hook. Prefer this over
// useWallet()'s isWalletEligibleByIdAndType whenever the caller already has the wallet
// object (e.g. filtering a list of TWalletData), so building an options list doesn't need
// to pull in useWallet()'s side effects (fetchWallets on mount, balance/card refresh
// effects) just to re-derive something already available.
export const isWalletEligible = (
  wallet: Pick<TWalletData, 'walletType'> | undefined,
  eligibleWalletTypes: WalletTypeEnum[]
): boolean =>
  !!wallet?.walletType && eligibleWalletTypes.includes(wallet.walletType as WalletTypeEnum);
